import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Clock, KeyRound, Mail } from "lucide-react";

import { authApi } from "@/features/auth/api";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { providersApi } from "@/features/settings/api";
import { cn } from "@/lib/utils";

const TONES = {
  amber:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
  blue: "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200",
  rose: "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200",
};

function Banner({
  tone,
  icon,
  children,
}: {
  tone: keyof typeof TONES;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm",
        TONES[tone],
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export default function AccountBanners() {
  const user = useAuthStore((s) => s.user);
  const [sending, setSending] = useState(false);
  const isAdmin = hasMinRole(user?.role, "admin");

  // New-tenant nudge: agents are mute/deaf until AI provider keys exist.
  const providersQ = useQuery({
    queryKey: ["providers", "list"],
    queryFn: providersApi.list,
    enabled: !!user && isAdmin,
    staleTime: 5 * 60_000,
  });
  const noProviderKeys =
    isAdmin && providersQ.isSuccess && providersQ.data.length === 0;

  if (!user) return null;
  const tenant = user.tenant;
  const unverified = !user.email_verified_at;
  const status = tenant?.status;
  const isTrial = !!tenant?.is_trial;
  const daysLeft = tenant?.trial_days_remaining ?? 0;

  async function resend() {
    setSending(true);
    try {
      await authApi.resendVerification();
      toast.success("Verification email sent — check your inbox.");
    } catch {
      toast.error("Couldn't send the email. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (!unverified && !isTrial && status !== "suspended" && !noProviderKeys)
    return null;

  return (
    <div className="mb-4 space-y-2">
      {noProviderKeys && (
        <Banner tone="amber" icon={<KeyRound className="size-4" />}>
          No AI provider keys yet — agents can't hear, think, or speak without
          them.{" "}
          <Link
            to="/settings?tab=providers"
            className="font-medium underline underline-offset-2"
          >
            Add your API keys
          </Link>{" "}
          (each provider shows where to find its key).
        </Banner>
      )}

      {unverified && (
        <Banner tone="amber" icon={<Mail className="size-4" />}>
          Please verify your email to keep full access.{" "}
          <button
            type="button"
            onClick={resend}
            disabled={sending}
            className="font-medium underline underline-offset-2 disabled:opacity-60"
          >
            {sending ? "Sending…" : "Resend link"}
          </button>
        </Banner>
      )}

      {status === "suspended" && (
        <Banner tone="rose" icon={<AlertTriangle className="size-4" />}>
          Your account is suspended.{" "}
          <Link
            to="/billing"
            className="font-medium underline underline-offset-2"
          >
            Choose a plan
          </Link>{" "}
          to continue using features.
        </Banner>
      )}

      {isTrial && status !== "suspended" && (
        <Banner tone="blue" icon={<Clock className="size-4" />}>
          {daysLeft > 0
            ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left in your free trial.`
            : "Your free trial has ended."}{" "}
          <Link
            to="/billing"
            className="font-medium underline underline-offset-2"
          >
            Choose a plan
          </Link>
        </Banner>
      )}
    </div>
  );
}
